<template>
  <div class="space-y-4">
    <div class="sm:flex sm:items-center sm:justify-between">
      <h2 class="text-2xl font-semibold text-gray-900">Debug Logs</h2>
    </div>

    <div class="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
      <div class="border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div class="flex items-center justify-between">
          <!-- Per page selector -->
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-700">Show:</span>
            <div class="flex space-x-1">
              <button 
                v-for="option in perPageOptions" 
                :key="option"
                @click="changePerPage(option)"
                :class="[
                  'relative inline-flex items-center px-3 py-1.5 text-sm font-medium',
                  'border border-gray-300 rounded-md transition-colors',
                  perPage === option 
                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                ]"
                type="button"
              >
                {{ option }}
              </button>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="flex items-center space-x-3">
            <button 
              @click="downloadLogs" 
              class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg class="mr-2 -ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Logs
            </button>

            <button 
              @click="clearLogs" 
              class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg class="mr-2 -ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear Logs
            </button>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table v-if="logs.length > 0" class="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Timestamp</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Level</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Message</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="log in logs" :key="log.timestamp" :class="[
              'hover:bg-gray-50',
              {
                'bg-red-900': log.level === 'critical',
                'bg-red-50': log.level === 'error',
                'bg-yellow-50': log.level === 'warning',
                'bg-blue-50': log.level === 'info'
              }
            ]">
              <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-left sm:pl-6" :class="log.level === 'critical' ? 'text-white' : 'text-gray-900'">{{ formatDate(log.datetime, log) }}</td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-left">
                <span :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  {
                    'bg-red-900 text-white border-2 border-red-600': log.level === 'critical',
                    'bg-red-100 text-red-800': log.level === 'error',
                    'bg-yellow-100 text-yellow-800': log.level === 'warning',
                    'bg-blue-100 text-blue-800': log.level === 'info',
                    'bg-gray-100 text-gray-800': log.level === 'debug'
                  }
                ]">
                  {{ log.level.toUpperCase() }}
                </span>
              </td>
              <td class="px-3 py-4 text-sm text-left break-all" :class="log.level === 'critical' ? 'text-white' : 'text-gray-500'">{{ log.message }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="text-center py-6 text-sm text-gray-500 bg-gray-50">
          {{ error || 'No logs found. Make sure debug mode is enabled and there are logs in the system.' }}
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center space-x-1 mt-4">
      <!-- Previous button -->
      <button
        @click="changePage(currentPage - 1)"
        :disabled="currentPage === 1"
        :class="[
          'relative inline-flex items-center px-2 py-2 text-sm font-medium rounded-md',
          currentPage === 1 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        ]"
        type="button"
      >
        <span class="sr-only">Previous</span>
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- First page -->
      <button
        v-if="showFirstPage"
        @click="changePage(1)"
        :class="[
          'relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md',
          currentPage === 1 
            ? 'z-10 bg-blue-600 text-white focus:z-20'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        ]"
        type="button"
      >
        1
      </button>

      <!-- Left ellipsis -->
      <span v-if="showLeftEllipsis" class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700">
        ...
      </span>

      <!-- Middle pages -->
      <button
        v-for="page in visiblePages"
        :key="page"
        @click="changePage(page)"
        :class="[
          'relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md',
          currentPage === page 
            ? 'z-10 bg-blue-600 text-white focus:z-20'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        ]"
        type="button"
      >
        {{ page }}
      </button>

      <!-- Right ellipsis -->
      <span v-if="showRightEllipsis" class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700">
        ...
      </span>

      <!-- Last page -->
      <button
        v-if="showLastPage"
        @click="changePage(totalPages)"
        :class="[
          'relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md',
          currentPage === totalPages 
            ? 'z-10 bg-blue-600 text-white focus:z-20'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        ]"
        type="button"
      >
        {{ totalPages }}
      </button>

      <!-- Next button -->
      <button
        @click="changePage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        :class="[
          'relative inline-flex items-center px-2 py-2 text-sm font-medium rounded-md',
          currentPage === totalPages 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-500 hover:bg-gray-50 focus:z-20'
        ]"
        type="button"
      >
        <span class="sr-only">Next</span>
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogViewer',
  data() {
    return {
      logs: [],
      currentPage: 1,
      totalPages: 0,
      totalLogs: 0,
      perPage: 10,
      perPageOptions: [5, 10, 25, 100],
      error: null,
      maxVisiblePages: 5 // Maximum number of page buttons to show at once
    }
  },
  inject: ['wpApi'],
  computed: {
    // Calculate which pages should be visible in the pagination
    visiblePages() {
      if (this.totalPages <= this.maxVisiblePages) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      const halfVisible = Math.floor(this.maxVisiblePages / 2);
      let start = Math.max(this.currentPage - halfVisible, 1);
      let end = Math.min(start + this.maxVisiblePages - 1, this.totalPages);

      if (end - start + 1 < this.maxVisiblePages) {
        start = Math.max(end - this.maxVisiblePages + 1, 1);
      }

      return Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
      );
    },
    // Show first page button if we're not at the start
    showFirstPage() {
      return this.visiblePages[0] > 1;
    },
    // Show last page button if we're not at the end
    showLastPage() {
      return this.visiblePages[this.visiblePages.length - 1] < this.totalPages;
    },
    // Show left ellipsis if there are hidden pages between first and visible
    showLeftEllipsis() {
      return this.showFirstPage && this.visiblePages[0] > 2;
    },
    // Show right ellipsis if there are hidden pages between visible and last
    showRightEllipsis() {
      return this.showLastPage && this.visiblePages[this.visiblePages.length - 1] < this.totalPages - 1;
    }
  },
  methods: {
    async fetchLogs() {
      try {
        this.error = null;
        const response = await this.wpApi.get('/weeconnectpay/v1/logs', {
          params: { 
            page: this.currentPage,
            per_page: this.perPage
          }
        });
        
        this.logs = response.data.logs;
        this.totalPages = response.data.pagination.total_pages;
        this.totalLogs = response.data.pagination.total_logs;
      } catch (error) {
        this.error = 'Error fetching logs. Please make sure debug mode is enabled and try again.';
        this.logs = [];
        this.totalPages = 0;
        this.totalLogs = 0;
      }
    },
    async downloadLogs() {
      try {
        const response = await this.wpApi.get('/weeconnectpay/v1/logs/download', {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `weeconnectpay-logs-${new Date().toISOString()}.txt`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading logs:', error.message);
      }
    },
    async clearLogs() {
      if (!confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
        return;
      }

      try {
        await this.wpApi.post('/weeconnectpay/v1/logs/clear');
        this.fetchLogs(); // Refresh the logs view
      } catch (error) {
        console.error('Error clearing logs:', error.message);
      }
    },
    changePage(page) {
      this.currentPage = page;
      this.fetchLogs();
    },
    changePerPage(newPerPage) {
      this.perPage = newPerPage;
      this.currentPage = 1; // Reset to first page when changing items per page
      this.fetchLogs();
    },
    formatDate(datetime, log) {
      try {
        // Extract timestamp from raw log entry
        if (log && log.raw) {
          const match = log.raw.match(/^\[([^\]]+)\]/);
          if (match) {
            return match[1];
          }
        }
        return datetime;
      } catch (e) {
        console.error('Error formatting date:', e);
        return datetime;
      }
    }
  },
  mounted() {
    this.fetchLogs();
  }
}
</script>

<style>
/* Remove scoped styles as we're using Tailwind classes */
</style> 